/**
 * Conversation Memory & Pinning — manages conversation auto-summarization
 * and user-pinned messages for AI chat context preservation.
 *
 * Features:
 * - Auto-summarize every 15 exchanges to compress context
 * - Store summary + last 5 messages in memory system
 * - User can pin important messages (preserved across summarization)
 * - Preserve user intent + completed actions in summary
 * - Idempotent: only summarize once per conversation
 */

import { saveEntry, deleteMemoryEntry, readEntry } from "@/services/ai-memory"
import type { ChatMessage } from "./useAiChat"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConversationSummary {
  conversationId: string
  messageCount: number
  summary: string
  userIntents: string[]
  completedActions: string[]
  pinnedMessageIds: string[]
  summarizedAt: string
}

export interface PinnedMessage {
  messageId: string
  conversationId: string
  role: "user" | "assistant"
  content: string
  reason?: string // why user pinned this
  pinnedAt: string
}

// ---------------------------------------------------------------------------
// Memory key management
// ---------------------------------------------------------------------------

function getSummaryKey(conversationId: string): string {
  return `summary_${conversationId}`
}

function getPinnedKey(conversationId: string, messageId: string): string {
  return `pinned_${conversationId}_${messageId}`
}

// ---------------------------------------------------------------------------
// Conversation summary operations
// ---------------------------------------------------------------------------

/**
 * Save conversation summary to AI memory system.
 * Stores: conversation context, user intents, completed actions, pinned messages.
 */
export async function saveConversationSummary(
  conversationId: string,
  messages: ChatMessage[],
  summary: string,
  userIntents: string[],
  completedActions: string[],
  pinnedMessageIds: string[],
): Promise<boolean> {
  try {
    const summaryData: ConversationSummary = {
      conversationId,
      messageCount: messages.length,
      summary,
      userIntents,
      completedActions,
      pinnedMessageIds,
      summarizedAt: new Date().toISOString(),
    }

    return await saveEntry(
      "custom",
      getSummaryKey(conversationId),
      {
        label: `Conversation ${conversationId.slice(0, 8)}...`,
        content: formatSummaryForMemory(summaryData),
        source: "explicit",
        confidence: 1.0,
        updatedAt: new Date().toISOString(),
      },
    )
  } catch (e) {
    console.error("[conversationMemory] Failed to save summary:", e)
    return false
  }
}

/**
 * Load conversation summary from memory if it exists.
 */
export async function loadConversationSummary(
  conversationId: string,
): Promise<ConversationSummary | null> {
  try {
    const entry = await readEntry("custom", getSummaryKey(conversationId))
    if (!entry) return null

    // Parse the summary content back into structured data
    // Format: "Summary: ...\nIntents: ...\nActions: ...\nPinned: ..."
    const lines = entry.content.split("\n")
    let summary = ""
    const userIntents: string[] = []
    const completedActions: string[] = []
    const pinnedMessageIds: string[] = []

    let currentSection = ""
    for (const line of lines) {
      if (line.startsWith("Summary: ")) {
        currentSection = "summary"
        summary = line.slice(9)
      } else if (line.startsWith("Intents: ")) {
        currentSection = "intents"
        const intents = line.slice(9).split(", ").filter(Boolean)
        userIntents.push(...intents)
      } else if (line.startsWith("Actions: ")) {
        currentSection = "actions"
        const actions = line.slice(9).split(", ").filter(Boolean)
        completedActions.push(...actions)
      } else if (line.startsWith("Pinned: ")) {
        currentSection = "pinned"
        const pinned = line.slice(8).split(", ").filter(Boolean)
        pinnedMessageIds.push(...pinned)
      } else if (currentSection === "summary" && line && !line.includes(":")) {
        summary += "\n" + line
      }
    }

    return {
      conversationId,
      messageCount: 0,
      summary: summary.trim(),
      userIntents,
      completedActions,
      pinnedMessageIds,
      summarizedAt: entry.updatedAt,
    }
  } catch (e) {
    console.error("[conversationMemory] Failed to load summary:", e)
    return null
  }
}

/**
 * Format conversation summary for storage in memory system.
 */
function formatSummaryForMemory(summary: ConversationSummary): string {
  const lines = [
    `Summary: ${summary.summary}`,
    `Intents: ${summary.userIntents.join(", ") || "N/A"}`,
    `Actions: ${summary.completedActions.join(", ") || "N/A"}`,
    `Pinned: ${summary.pinnedMessageIds.join(", ") || "none"}`,
  ]
  return lines.join("\n")
}

// ---------------------------------------------------------------------------
// Message pinning operations
// ---------------------------------------------------------------------------

/**
 * Pin a message so it stays in context across future exchanges.
 * Used when user explicitly marks a message as important.
 */
export async function pinMessage(
  conversationId: string,
  message: ChatMessage,
  reason?: string,
): Promise<boolean> {
  try {
    const pinnedMsg: PinnedMessage = {
      messageId: message.id,
      conversationId,
      role: message.role,
      content: message.content,
      reason,
      pinnedAt: new Date().toISOString(),
    }

    return await saveEntry(
      "custom",
      getPinnedKey(conversationId, message.id),
      {
        label: `Pinned: ${message.content.slice(0, 50)}...`,
        content: JSON.stringify(pinnedMsg),
        source: "explicit",
        confidence: 1.0,
        updatedAt: new Date().toISOString(),
      },
    )
  } catch (e) {
    console.error("[conversationMemory] Failed to pin message:", e)
    return false
  }
}

/**
 * Unpin a message.
 */
export async function unpinMessage(
  conversationId: string,
  messageId: string,
): Promise<boolean> {
  try {
    return await deleteMemoryEntry("custom", getPinnedKey(conversationId, messageId))
  } catch (e) {
    console.error("[conversationMemory] Failed to unpin message:", e)
    return false
  }
}

/**
 * Get all pinned messages for a conversation.
 */
export async function getPinnedMessages(conversationId: string): Promise<PinnedMessage[]> {
  try {
    const entry = await readEntry("custom", getSummaryKey(conversationId))
    if (!entry) return []

    // Extract pinned message IDs from summary
    const lines = entry.content.split("\n")
    const pinnedLine = lines.find((l) => l.startsWith("Pinned:"))
    if (!pinnedLine) return []

    const pinnedIds = pinnedLine.slice(8).split(", ").filter(Boolean)
    const pinnedMessages: PinnedMessage[] = []

    for (const msgId of pinnedIds) {
      const pinnedEntry = await readEntry("custom", getPinnedKey(conversationId, msgId))
      if (pinnedEntry) {
        try {
          const parsed = JSON.parse(pinnedEntry.content) as PinnedMessage
          pinnedMessages.push(parsed)
        } catch {
          // Skip malformed entries
        }
      }
    }

    return pinnedMessages
  } catch (e) {
    console.error("[conversationMemory] Failed to get pinned messages:", e)
    return []
  }
}

// ---------------------------------------------------------------------------
// Extract intent & actions from messages for summarization
// ---------------------------------------------------------------------------

/**
 * Extract user intents from a conversation.
 * Scans for patterns like "I want to...", "I need to...", "Plan to...".
 */
export function extractUserIntents(messages: ChatMessage[]): string[] {
  const intents: Set<string> = new Set()
  const intentPatterns = [
    /(?:want to|need to|planning to|plan to|going to|will)\s+([^.!?]+)/gi,
    /(?:help me|assist with|need help)\s+([^.!?]+)/gi,
    /(?:create|set up|organize|structure)\s+([^.!?]+)/gi,
  ]

  for (const msg of messages) {
    if (msg.role !== "user") continue
    const text = msg.content.toLowerCase()

    for (const pattern of intentPatterns) {
      let match
      while ((match = pattern.exec(text)) !== null) {
        const matchGroup = match[1]
        if (matchGroup !== undefined && matchGroup !== null) {
          const intent = String(matchGroup).trim()
          if (intent.length > 3 && intent.length < 100) {
            // Normalize: remove extra punctuation, make concise
            const normalized = intent
              .replace(/[?,;:!.]+$/, "")
              .slice(0, 50)
              .trim()
            if (normalized) intents.add(normalized)
          }
        }
      }
    }
  }

  return Array.from(intents).slice(0, 5) // Max 5 intents
}

/**
 * Extract completed actions from messages.
 * Scans assistant responses for completion signals like "Created", "Scheduled", "Moved".
 */
export function extractCompletedActions(messages: ChatMessage[]): string[] {
  const actions: Set<string> = new Set()
  const actionPatterns = [
    /(?:created?|added?|made?|set up|scheduled)\s+(?:a|the)?\s*([^.!?]+)/gi,
    /(?:moved|moved?|reorganized?|reordered?)\s+(?:the)?\s*([^.!?]+)/gi,
    /(?:completed?|finished?|marked as done)\s+(?:the)?\s*([^.!?]+)/gi,
    /✓\s*([^.!?\n]+)/gi, // Check marks
  ]

  for (const msg of messages) {
    if (msg.role !== "assistant") continue
    const text = msg.content

    for (const pattern of actionPatterns) {
      let match
      while ((match = pattern.exec(text)) !== null) {
        const matchGroup = match[1]
        if (matchGroup !== undefined && matchGroup !== null) {
          const action = String(matchGroup).trim()
          if (action.length > 3 && action.length < 100) {
            const normalized = action
              .replace(/[?,;:!.]+$/, "")
              .slice(0, 50)
              .trim()
            if (normalized && !normalized.includes("Failed")) actions.add(normalized)
          }
        }
      }
    }
  }

  return Array.from(actions).slice(0, 5) // Max 5 actions
}

// ---------------------------------------------------------------------------
// Build context from previous summaries
// ---------------------------------------------------------------------------

/**
 * Build a system prompt section that includes past conversation context.
 * Used to restore context across separate conversations.
 */
export function buildConversationHistoryPrompt(summaries: ConversationSummary[]): string {
  if (summaries.length === 0) return ""

  const sections = summaries
    .slice(0, 3) // Include last 3 conversation summaries
    .map(
      (s) =>
        `## Previous: ${s.summarizedAt.slice(0, 10)}\n${s.summary}\n` +
        `Intents: ${s.userIntents.join(", ") || "N/A"}\n` +
        `Completed: ${s.completedActions.join(", ") || "N/A"}`,
    )
    .join("\n\n")

  return `--- CONVERSATION HISTORY ---\n${sections}\n--- END HISTORY ---`
}
